import React, { useState, useEffect, useCallback } from "react"
import { styled }       from "@mui/system"
import Grid             from "@mui/material/Grid2"
import Card             from "@mui/material/Card"
import CardContent      from "@mui/material/CardContent"
import FormLabel        from "@mui/material/FormLabel"
import TextField        from "@mui/material/TextField"
import OutlinedInput    from "@mui/material/OutlinedInput"
import Autocomplete     from "@mui/material/Autocomplete"
import Select           from "@mui/material/Select"
import MenuItem         from "@mui/material/MenuItem"
import IconButton       from "@mui/material/IconButton"
import Button           from "@mui/material/Button"
import Alert            from "@mui/material/Alert"
import Skeleton         from "@mui/material/Skeleton"
import CircularProgress from "@mui/material/CircularProgress"
import SearchIcon       from "@mui/icons-material/Search"
import dayjs            from "dayjs"

// ─── Styled helpers ──────────────────────────────────────────────────────────

const FormGrid = styled(Grid)(() => ({
    display: "flex",
    flexDirection: "column",
}))

// ─── Constants ───────────────────────────────────────────────────────────────

const ENERGY_LABELS = ["A", "B", "C", "D", "E", "F", "G"]

const DEFAULT_DPE = {
    property_id:                 null,
    official_id:                 "",
    made_on:                     "",
    valid_until:                 "",
    energy_consumption:          "",
    energy_consumption_category: "",
    greenhouse_gas:              "",
    greenhouse_gas_category:     "",
}

// ─── Error message mapping ───────────────────────────────────────────────────
// PostgreSQL error codes returned by Supabase are mapped to user-friendly
// French sentences. Unknown codes fall back to the raw server message.
const PG_ERROR_MESSAGES = {
    "23503": "Suppression impossible : ce DPE est utilisé par d'autres données (contrainte de clé étrangère).",
    "23505": "Un DPE avec ce numéro officiel existe déjà.",
    "23502": "Des champs obligatoires sont manquants.",
    "42501": "Vous n'avez pas les droits nécessaires pour effectuer cette action.",
    "PGRST116": "Aucun DPE trouvé pour cet identifiant.",
}

function parseError(error) {
    if (!error) return null
    // Supabase wraps PG errors; the code can be at error.code or error.details
    const code = error.code ?? ""
    return PG_ERROR_MESSAGES[code] ?? error.message ?? "Une erreur inattendue s'est produite."
}

// ─── Skeleton field ──────────────────────────────────────────────────────────

function FieldSkeleton({ height = 40 }) {
    return (
        <Skeleton
            variant="rectangular"
            height={height}
            sx={{ borderRadius: 1, mt: 0.5 }}
        />
    )
}

// ─── Safe date formatter ─────────────────────────────────────────────────────

function toInputDate(value) {
    if (!value) return ""
    const d = dayjs(value)
    return d.isValid() ? d.format("YYYY-MM-DD") : ""
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function DPEForm({ supabase, selectedDPEId, onSuccess }) {

    // ── Properties (for autocomplete) ────────────────────────────────────────
    // Fetched once on mount; properties rarely change so no real-time sub needed.
    const [properties,        setProperties]        = useState([])
    const [propertiesLoading, setPropertiesLoading] = useState(true)

    // ── Form state ───────────────────────────────────────────────────────────
    const [DPE,        setDPE]        = useState(DEFAULT_DPE)
    const [dpeLoading, setDpeLoading] = useState(false)

    // ── UI feedback ──────────────────────────────────────────────────────────
    const [submitting,      setSubmitting]      = useState(false)
    const [ademeLoading,    setAdemeLoading]    = useState(false)
    const [error,           setError]           = useState(null)

    // ── Fetch properties once ─────────────────────────────────────────────────
    useEffect(() => {
        let cancelled = false
        ;(async () => {
            const { data, error } = await supabase
                .from("property")
                .select("id, short_name")
                .order("id")
            if (!cancelled) {
                if (!error) setProperties(data ?? [])
                setPropertiesLoading(false)
            }
        })()
        return () => { cancelled = true }
    }, [supabase])

    // ── Fetch selected DPE ────────────────────────────────────────────────────
    // Runs whenever selectedDPEId changes. Using a cancellation flag prevents
    // stale responses from overwriting state if the user selects another row
    // before the previous request completes.
    useEffect(() => {
        if (!selectedDPEId) {
            setDPE(DEFAULT_DPE)
            setError(null)
            return
        }

        let cancelled = false
        setDpeLoading(true)
        setError(null)

        ;(async () => {
            const { data, error } = await supabase
                .from("dpe")
                .select("*")
                .eq("id", selectedDPEId)
                .single()

            if (!cancelled) {
                if (error) {
                    setError(parseError(error))
                    setDPE(DEFAULT_DPE)
                } else {
                    setDPE(data ?? DEFAULT_DPE)
                }
                setDpeLoading(false)
            }
        })()

        return () => { cancelled = true }
    }, [selectedDPEId, supabase])

    // ── Input handlers ────────────────────────────────────────────────────────

    const handleChange = useCallback((e) => {
        const { name, value } = e.target
        setDPE((prev) => ({ ...prev, [name]: value }))
    }, [])

    // Date inputs return a YYYY-MM-DD string – store it directly; Supabase
    // accepts ISO date strings for date/timestamptz columns.
    const handleDateChange = useCallback((e) => {
        const { name, value } = e.target
        setDPE((prev) => ({ ...prev, [name]: value || null }))
    }, [])

    // ── ADEME lookup ──────────────────────────────────────────────────────────
    // Queries both the "existing" and "new construction" ADEME datasets in
    // parallel and merges results, matching the original implementation.
    const handleFetchDPE = async () => {
        if (!DPE?.official_id?.trim()) return
        setAdemeLoading(true)
        setError(null)

        try {
            const query = encodeURIComponent(`numero_dpe:${DPE.official_id}`)
            const urls = [
                `https://data.ademe.fr/data-fair/api/v1/datasets/dpe03existant/lines?qs=${query}`,
                `https://data.ademe.fr/data-fair/api/v1/datasets/dpe02neuf/lines?qs=${query}`,
            ]

            const responses = await Promise.all(urls.map((url) => fetch(url)))
            const payloads  = await Promise.all(
                responses.map(async (res) => {
                    if (!res.ok) throw new Error(`Erreur HTTP ${res.status}`)
                    return res.json()
                })
            )

            const results = payloads.flatMap((p) => p.results ?? [])

            if (results.length === 0) {
                setError("Aucun DPE trouvé pour ce numéro dans la base ADEME.")
                return
            }

            const r = results[0]
            setDPE((prev) => ({
                ...prev,
                official_id:                 r["numero_dpe"]               ?? prev.official_id,
                made_on:                     r["date_etablissement_dpe"]    ?? "",
                valid_until:                 r["date_fin_validite_dpe"]     ?? "",
                energy_consumption:          123,   // not exposed by ADEME; kept as placeholder
                energy_consumption_category: r["etiquette_dpe"]             ?? "",
                greenhouse_gas:              123,   // idem
                greenhouse_gas_category:     r["etiquette_ges"]             ?? "",
            }))
        } catch (err) {
            setError("Impossible de récupérer les données ADEME : " + err.message)
        } finally {
            setAdemeLoading(false)
        }
    }

    // ── Submit (insert or update) ─────────────────────────────────────────────
    const handleSubmit = async () => {
        setSubmitting(true)
        setError(null)

        try {
            let result

            if (selectedDPEId) {
                // Strip server-managed columns before sending an update
                const { id, created_at, updated_at, ...payload } = DPE
                result = await supabase
                    .from("dpe")
                    .update(payload)
                    .eq("id", selectedDPEId)
            } else {
                result = await supabase.from("dpe").insert(DPE)
            }

            if (result.error) {
                setError(parseError(result.error))
            } else {
                setDPE(DEFAULT_DPE)
                onSuccess?.()
            }
        } catch (err) {
            setError("Une erreur inattendue s'est produite.")
        } finally {
            setSubmitting(false)
        }
    }

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleDelete = async () => {
        setSubmitting(true)
        setError(null)

        try {
            const result = await supabase
                .from("dpe")
                .delete()
                .eq("id", selectedDPEId)

            if (result.error) {
                // FK violations (23503) produce a user-friendly message via parseError
                setError(parseError(result.error))
            } else {
                setDPE(DEFAULT_DPE)
                onSuccess?.()
            }
        } catch (err) {
            setError("Une erreur inattendue s'est produite.")
        } finally {
            setSubmitting(false)
        }
    }

    // ── Derived flags ─────────────────────────────────────────────────────────

    // Show per-field skeletons while the selected DPE or the properties list
    // are being fetched. We keep the Card mounted so the layout doesn't jump.
    const showSkeletons = dpeLoading || propertiesLoading

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Card variant="outlined">
            <CardContent>

                {/* ── Feedback alerts ──────────────────────────────────── */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                <Grid container spacing={2}>

                    {/* ── Bien (property) ──────────────────────────────── */}
                    <FormGrid size={{ xs: 12, md: 5, xl: 4 }}>
                        <FormLabel required>Bien</FormLabel>
                        {showSkeletons ? <FieldSkeleton /> : (
                            <Autocomplete
                                disablePortal
                                disableClearable
                                options={properties}
                                getOptionLabel={(opt) => opt?.short_name ?? ""}
                                value={properties.find((p) => p.id === DPE.property_id) ?? null}
                                onChange={(_, v) =>
                                    setDPE((prev) => ({ ...prev, property_id: v?.id ?? null }))
                                }
                                renderInput={(params) => (
                                    <TextField {...params} placeholder="HONO-55" size="small" />
                                )}
                            />
                        )}
                    </FormGrid>

                    {/* ── Numéro de DPE + ADEME lookup ─────────────────── */}
                    <FormGrid size={{ xs: 12, md: 5, xl: 4 }}>
                        <FormLabel htmlFor="official-id" required>Numéro de DPE</FormLabel>
                        {showSkeletons ? <FieldSkeleton /> : (
                            <OutlinedInput
                                id="official-id"
                                name="official_id"
                                placeholder="0123A9876543Z"
                                value={DPE.official_id}
                                onChange={handleChange}
                                size="small"
                            />
                        )}
                    </FormGrid>

                    {/* Search button – bottom-aligned with the input beside it */}
                    <FormGrid size={{ xs: 1 }} sx={{ justifyContent: "flex-end" }}>
                        {showSkeletons ? <FieldSkeleton height={36} /> : (
                            <IconButton
                                onClick={handleFetchDPE}
                                disabled={ademeLoading || !DPE.official_id?.trim()}
                                size="small"
                            >
                                {ademeLoading
                                    ? <CircularProgress size={18} />
                                    : <SearchIcon fontSize="small" />
                                }
                            </IconButton>
                        )}
                    </FormGrid>

                    {/* ── Dates ────────────────────────────────────────── */}
                    <FormGrid size={{ xs: 6 }}>
                        <FormLabel htmlFor="madeOn" required>Date d'établissement</FormLabel>
                        {showSkeletons ? <FieldSkeleton /> : (
                            <OutlinedInput
                                id="madeOn"
                                name="made_on"
                                type="date"
                                value={toInputDate(DPE.made_on)}
                                onChange={handleDateChange}
                                size="small"
                            />
                        )}
                    </FormGrid>

                    <FormGrid size={{ xs: 6 }}>
                        <FormLabel htmlFor="validUntil" required>Date de fin de validité</FormLabel>
                        {showSkeletons ? <FieldSkeleton /> : (
                            <OutlinedInput
                                id="validUntil"
                                name="valid_until"
                                type="date"
                                value={toInputDate(DPE.valid_until)}
                                onChange={handleDateChange}
                                size="small"
                            />
                        )}
                    </FormGrid>

                    {/* ── Énergie ──────────────────────────────────────── */}
                    <FormGrid size={{ xs: 8 }}>
                        <FormLabel htmlFor="energyConsumption" required>
                            Consommation (énergie primaire)
                        </FormLabel>
                        {showSkeletons ? <FieldSkeleton /> : (
                            <OutlinedInput
                                id="energyConsumption"
                                name="energy_consumption"
                                placeholder="123"
                                endAdornment="kWh/m²/an"
                                value={DPE.energy_consumption}
                                onChange={handleChange}
                                size="small"
                                type="number"
                            />
                        )}
                    </FormGrid>

                    <FormGrid size={{ xs: 4 }}>
                        <FormLabel htmlFor="energyCategory" required>Etiquette DPE</FormLabel>
                        {showSkeletons ? <FieldSkeleton /> : (
                            <Select
                                id="energyCategory"
                                name="energy_consumption_category"
                                value={DPE.energy_consumption_category}
                                onChange={handleChange}
                                size="small"
                            >
                                {ENERGY_LABELS.map((x) => (
                                    <MenuItem key={x} value={x}>{x}</MenuItem>
                                ))}
                            </Select>
                        )}
                    </FormGrid>

                    {/* ── GES ──────────────────────────────────────────── */}
                    <FormGrid size={{ xs: 8 }}>
                        <FormLabel htmlFor="greenhouseGas" required>Emissions</FormLabel>
                        {showSkeletons ? <FieldSkeleton /> : (
                            <OutlinedInput
                                id="greenhouseGas"
                                name="greenhouse_gas"
                                placeholder="123"
                                endAdornment="kg CO2/m²/an"
                                value={DPE.greenhouse_gas}
                                onChange={handleChange}
                                size="small"
                                type="number"
                            />
                        )}
                    </FormGrid>

                    <FormGrid size={{ xs: 4 }}>
                        <FormLabel htmlFor="gesCategory" required>Etiquette GES</FormLabel>
                        {showSkeletons ? <FieldSkeleton /> : (
                            <Select
                                id="gesCategory"
                                name="greenhouse_gas_category"
                                value={DPE.greenhouse_gas_category}
                                onChange={handleChange}
                                size="small"
                            >
                                {ENERGY_LABELS.map((x) => (
                                    <MenuItem key={x} value={x}>{x}</MenuItem>
                                ))}
                            </Select>
                        )}
                    </FormGrid>

                    {/* ── Action buttons ────────────────────────────────── */}
                    <FormGrid
                        size={{ xs: selectedDPEId ? 6 : 12 }}
                        sx={{ mt: 1 }}
                    >
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={submitting || showSkeletons}
                            startIcon={submitting && <CircularProgress size={16} color="inherit" />}
                        >
                            Valider
                        </Button>
                    </FormGrid>

                    {selectedDPEId && (
                        <FormGrid size={{ xs: 6 }} sx={{ mt: 1 }}>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={handleDelete}
                                disabled={submitting || showSkeletons}
                            >
                                Supprimer
                            </Button>
                        </FormGrid>
                    )}

                </Grid>
            </CardContent>
        </Card>
    )
}